import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { 
  Send,
  Package,
  MapPin,
  Calendar,
  X,
  CheckCircle,
  AlertCircle,
  Clock,
  Timer,
  DollarSign,
  Weight,
  User,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Plus,
  FileText,
  Inbox,
  ArrowRight,
  Mail,
  Trash2,
  Truck
} from 'lucide-react';
import { applicationsAPI } from '../services/api';
import { useThemeColors } from '../theme/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import ConfirmationModal from '../components/ConfirmationModal';

const Applications = () => {
  const colors = useThemeColors();
  const navigate = useNavigate();
  const [mainTab, setMainTab] = useState('sent'); // 'sent' or 'received'
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [totalItems, setTotalItems] = useState(0);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationModalConfig, setConfirmationModalConfig] = useState({});

  // Reset page when filters, search, or main tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab, mainTab]);

  const queryClient = useQueryClient();

  // Fetch applications based on main tab
  const {
    data: apiResponse,
    isLoading,
    refetch,
  } = useQuery(
    ['applications', mainTab, currentPage, itemsPerPage, searchTerm, activeTab],
    () => {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        status: activeTab === 'all' ? '' : activeTab
      };
      
      return mainTab === 'sent' 
        ? applicationsAPI.getUserApplications(params)
        : applicationsAPI.getReceivedApplications(params);
    },
    {
      select: (response) => {
        // If API returns data, use it; otherwise use dummy data
        const apiData = response?.data?.data || {};
        if (apiData.applications && apiData.applications.length > 0) {
          return {
            applications: apiData.applications,
            pagination: apiData.pagination,
            statusCounts: apiData.statusCounts
          };
        }
        // Use dummy data as fallback
        return {
          applications: [],
          pagination: null,
          statusCounts: {}
        };
      },
    }
  );
  console.log(apiResponse);
  
  const applications = apiResponse?.applications || [];
  const paginationData = apiResponse?.pagination;
  const statusCounts = apiResponse?.statusCounts || {};
  // Cancel application mutation
  const cancelApplicationMutation = useMutation(applicationsAPI.cancelApplication, {
    onSuccess: () => {
      toast.success('Application cancelled successfully!');
      queryClient.invalidateQueries(['applications', mainTab]);
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to cancel application');
    },
  });

  // Accept application mutation
  const acceptApplicationMutation = useMutation(applicationsAPI.acceptApplication, {
    onSuccess: () => {
      toast.success('Application accepted successfully!');
      queryClient.invalidateQueries(['applications', mainTab]);
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to accept application');
    },
  });

  // Reject application mutation
  const rejectApplicationMutation = useMutation(applicationsAPI.rejectApplication, {
    onSuccess: () => {
      toast.success('Application rejected successfully!');
      queryClient.invalidateQueries(['applications', mainTab]);
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reject application');
    },
  });

  // Delete application mutation
  const deleteApplicationMutation = useMutation(applicationsAPI.deleteApplication, {
    onSuccess: () => {
      toast.success('Application deleted successfully!');
      queryClient.invalidateQueries(['applications', mainTab]);
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete application');
    },
  });

  const handleCancelApplication = (applicationId) => {
    setConfirmationModalConfig({
      isOpen: true,
      type: 'error',
      title: 'Cancel Application',
      message: 'Are you sure you want to cancel this application? This action cannot be undone.',
      confirmText: 'Cancel Application',
      cancelText: 'Back',
      showInput: false,
      onConfirm: () => {
        cancelApplicationMutation.mutate(applicationId);
        setShowConfirmationModal(false);
      }
    });
    setShowConfirmationModal(true);
  };

  const handleAcceptApplication = (applicationId) => {
    // Find the application to get declared_amount and target_type
    const application = applications.find(app => app.id === applicationId);
    
    // Different message for parcel vs trip applications
    const isParcel = application?.target_type === 'parcel';
    const confirmationMessage = isParcel 
      ? 'Are you sure you want to accept this application? This will create an agreement with the applicant and notify all other applicants that their applications have been declined.'
      : 'Are you sure you want to accept this application? This will create an agreement with the applicant. If this fills your trip capacity, remaining applications will be automatically declined.';
    
    setConfirmationModalConfig({
      isOpen: true,
      type: 'success',
      title: 'Accept Application',
      message: confirmationMessage,
      confirmText: 'Accept Application',
      cancelText: 'Cancel',
      showInput: false,
      additionalInfo: application?.declared_amount 
        ? `Declared Amount: ${application.currency === 'USD' ? '$' : 'ETB'}${application.declared_amount}` 
        : null,
      onConfirm: () => {
        acceptApplicationMutation.mutate(applicationId);
        setShowConfirmationModal(false);
      }
    });
    setShowConfirmationModal(true);
  };

  const handleRejectApplication = (applicationId) => {
    let inputValue = '';
    setConfirmationModalConfig({
      isOpen: true,
      type: 'warning',
      title: 'Reject Application',
      message: 'Please provide a reason for rejecting this application (optional):',
      confirmText: 'Reject Application',
      cancelText: 'Cancel',
      showInput: true,
      inputValue: inputValue,
      inputPlaceholder: 'e.g., Another applicant was more suitable, schedule conflict, etc.',
      onConfirm: (reason) => {
        rejectApplicationMutation.mutate(applicationId, { reason });
        setShowConfirmationModal(false);
      },
      onInputChange: (value) => {
        inputValue = value;
        setConfirmationModalConfig(prev => ({ ...prev, inputValue: value }));
      }
    });
    setShowConfirmationModal(true);
  };

  const handleDeleteApplication = (applicationId) => {
    setConfirmationModalConfig({
      isOpen: true,
      type: 'error',
      title: 'Delete Application',
      message: 'Are you sure you want to delete this application? This will remove it from your applications list.',
      confirmText: 'Delete Application',
      cancelText: 'Keep Application',
      showInput: false,
      onConfirm: () => {
        deleteApplicationMutation.mutate(applicationId);
        setShowConfirmationModal(false);
      }
    });
    setShowConfirmationModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'accepted':
        return <CheckCircle className="h-4 w-4" />;
      case 'rejected':
        return <X className="h-4 w-4" />;
      case 'cancelled':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const filteredApplications = applications; // Server-side filtering is now used
  console.log(filteredApplications);
  
  const stats = {
    total: statusCounts?.total || 0,
    pending: statusCounts.pending || 0,
    accepted: statusCounts.accepted || 0,
    rejected: statusCounts.rejected || 0,
    cancelled: statusCounts.cancelled || 0,
  };

  // Pagination calculations
  const totalPages = paginationData?.totalPages || Math.ceil(applications.length / itemsPerPage);
  const startItem = ((currentPage - 1) * itemsPerPage) + 1;
  const endItem = Math.min(currentPage * itemsPerPage, paginationData?.total || applications.length);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-x-hidden">
        <SEO 
          title="Applications" 
          description="Manage your trip applications and delivery requests. Track sent and received applications on TripTrot"
          keywords={['trip applications', 'delivery requests', 'application management', 'parcel delivery applications']}
          canonical="/applications"
          structuredData={{
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Applications",
            "description": "Manage trip applications and delivery requests"
          }}
        />
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-theme-primary">Applications</h1>
          <p className="mt-2 text-theme-secondary">
            {mainTab === 'sent' 
              ? 'Track your delivery applications and their status'
              : 'Review applications received for your trips and parcels'
            }
          </p>
        </div>

        {/* Main Tabs */}
        <div className="mb-8">
          {/* Mobile: Vertical tabs | Desktop: Horizontal tabs */}
          <div className="sm:hidden">
            <div 
              className="rounded-2xl p-2 space-y-1 border"
              style={{ 
                backgroundColor: colors.card, 
                borderColor: colors.border 
              }}
            >
              {[
                { key: 'sent', label: 'Sent Applications', icon: Send },
                { key: 'received', label: 'Received Applications', icon: Inbox }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setMainTab(tab.key)}
                  className={`flex items-center w-full px-3 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                    mainTab === tab.key
                      ? 'shadow-lg'
                      : ''
                  }`}
                  style={{
                    backgroundColor: mainTab === tab.key ? colors.primary[600] : 'transparent',
                    color: mainTab === tab.key ? 'white' : colors.text.secondary
                  }}
                  onMouseEnter={(e) => {
                    if (mainTab !== tab.key) {
                      e.currentTarget.style.backgroundColor = colors.neutral[50];
                      e.currentTarget.style.color = colors.primary[600];
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (mainTab !== tab.key) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = colors.text.secondary;
                    }
                  }}
                >
                  <tab.icon className="h-4 w-4 mr-3 flex-shrink-0" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Desktop version */}
          <div className="hidden sm:block">
            <div 
              className="rounded-2xl p-2 flex flex-wrap gap-2 items-center justify-around"
              style={{ 
                backgroundColor: colors.card, 
                borderColor: colors.border 
              }}
            >
              {[
                { key: 'sent', label: 'Sent Applications', icon: Send },
                { key: 'received', label: 'Received Applications', icon: Inbox }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setMainTab(tab.key)}
                  className={`flex items-center px-4 py-2 rounded-xl font-medium text-sm transition-all duration-200 flex-1 min-w-0 sm:flex-none ${
                    mainTab === tab.key
                      ? 'shadow-lg'
                      : ''
                  }`}
                  style={{
                    backgroundColor: mainTab === tab.key ? colors.primary[600] : 'transparent',
                    color: mainTab === tab.key ? 'white' : colors.text.secondary
                  }}
                  onMouseEnter={(e) => {
                    if (mainTab !== tab.key) {
                      e.currentTarget.style.backgroundColor = colors.neutral[50];
                      e.currentTarget.style.color = colors.primary[600];
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (mainTab !== tab.key) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = colors.text.secondary;
                    }
                  }}
                >
                  <tab.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-8">
          {/* Main Search Card */}
          <div 
            className="rounded-2xl shadow-xl transition-all duration-300 overflow-hidden"
            style={{ 
              backgroundColor: colors.card, 
              borderColor: colors.border 
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = colors.primary[300];
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = colors.border;
            }}
          >
            {/* Top accent bar */}
            <div className="h-1" />
            
            {/* Content */}
            <div className="p-6">
              {/* Search Row */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search 
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5" 
                    style={{ color: colors.text.secondary }}
                  />
                  <input
                    type="text"
                    placeholder={
                      mainTab === 'sent' 
                        ? "Search applications by city or description..."
                        : "Search applications by applicant name, email, or message..."
                    }
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 pr-4 py-3 w-full rounded-xl transition-all duration-200 border"
                    style={{
                      backgroundColor: colors.neutral[50],
                      borderColor: colors.border,
                      color: colors.text.primary
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = colors.primary[500];
                      e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary[200]}`;
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = colors.border;
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-theme-card p-4 rounded-lg border border-theme-light">
            <div className="flex items-center">
              <div className="p-2 bg-theme-primary-100 rounded-lg">
                <Send className="h-5 w-5 text-theme-primary" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-theme-secondary">Total</p>
                <p className="text-lg font-semibold text-theme-primary">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-theme-card p-4 rounded-lg border border-theme-light">
            <div className="flex items-center">
              <div className="p-2 bg-theme-warning-100 rounded-lg">
                <Clock className="h-5 w-5 text-theme-warning" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-theme-secondary">Pending</p>
                <p className="text-lg font-semibold text-theme-primary">{stats.pending}</p>
              </div>
            </div>
          </div>

          <div className="bg-theme-card p-4 rounded-lg border border-theme-light">
            <div className="flex items-center">
              <div className="p-2 bg-theme-success-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-theme-success" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-theme-secondary">Accepted</p>
                <p className="text-lg font-semibold text-theme-primary">{stats.accepted}</p>
              </div>
            </div>
          </div>

          <div className="bg-theme-card p-4 rounded-lg border border-theme-light">
            <div className="flex items-center">
              <div className="p-2 bg-theme-error-100 rounded-lg">
                <X className="h-5 w-5 text-theme-error" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-theme-secondary">Rejected</p>
                <p className="text-lg font-semibold text-theme-primary">{stats.rejected}</p>
              </div>
            </div>
          </div>

          <div className="bg-theme-card p-4 rounded-lg border border-theme-light">
            <div className="flex items-center">
              <div className="p-2 bg-theme-neutral-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-theme-neutral" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-theme-secondary">Cancelled</p>
                <p className="text-lg font-semibold text-theme-primary">{stats.cancelled}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sleek Tabs */}
        <div className="mb-8">
          {/* Mobile: Vertical tabs | Desktop: Horizontal tabs */}
          <div className="sm:hidden">
            <div 
              className="rounded-2xl p-2 space-y-1 border"
              style={{ 
                backgroundColor: colors.card, 
                borderColor: colors.border 
              }}
            >
              {[
                { key: 'all', label: 'All', icon: FileText },
                { key: 'pending', label: 'Pending', icon: Clock },
                { key: 'accepted', label: 'Accepted', icon: CheckCircle },
                { key: 'rejected', label: 'Rejected', icon: X },
                { key: 'cancelled', label: 'Cancelled', icon: AlertCircle }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center w-full px-3 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                    activeTab === tab.key
                      ? 'shadow-lg'
                      : ''
                  }`}
                  style={{
                    backgroundColor: activeTab === tab.key ? colors.primary[600] : 'transparent',
                    color: activeTab === tab.key ? 'white' : colors.text.secondary
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab.key) {
                      e.currentTarget.style.backgroundColor = colors.neutral[50];
                      e.currentTarget.style.color = colors.primary[600];
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab.key) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = colors.text.secondary;
                    }
                  }}
                >
                  <tab.icon className="h-4 w-4 mr-3 flex-shrink-0" />
                  <span className="flex-1 text-left">{tab.label}</span>
                  {tab.key !== 'all' && stats[tab.key] > 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs flex-shrink-0 ${
                      activeTab === tab.key 
                        ? 'bg-white/20 text-white' 
                        : 'bg-theme-neutral-25 text-theme-secondary'
                    }`}>
                      {stats[tab.key]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Desktop version */}
          <div className="hidden sm:block">
            <div 
              className="rounded-2xl p-2 flex flex-wrap gap-2 items-center justify-around"
              style={{ 
                backgroundColor: colors.card, 
                borderColor: colors.border 
              }}
            >
              {[
                { key: 'all', label: 'All', icon: FileText },
                { key: 'pending', label: 'Pending', icon: Clock },
                { key: 'accepted', label: 'Accepted', icon: CheckCircle },
                { key: 'rejected', label: 'Rejected', icon: X },
                { key: 'cancelled', label: 'Cancelled', icon: AlertCircle }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center px-4 py-2 rounded-xl font-medium text-sm transition-all duration-200 flex-1 min-w-0 sm:flex-none ${
                    activeTab === tab.key
                      ? 'shadow-lg'
                      : ''
                  }`}
                  style={{
                    backgroundColor: activeTab === tab.key ? colors.primary[600] : 'transparent',
                    color: activeTab === tab.key ? 'white' : colors.text.secondary
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab.key) {
                      e.currentTarget.style.backgroundColor = colors.neutral[50];
                      e.currentTarget.style.color = colors.primary[600];
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab.key) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = colors.text.secondary;
                    }
                  }}
                >
                  <tab.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{tab.label}</span>
                  {tab.key !== 'all' && stats[tab.key] > 0 && (
                    <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
                      activeTab === tab.key 
                        ? 'bg-white/20 text-white' 
                        : 'bg-theme-neutral-25 text-theme-secondary'
                    }`}>
                      {stats[tab.key]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Applications Grid */}
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-sm text-gray-500">Loading applications...</p>
          </div>
        ) : !filteredApplications || filteredApplications.length === 0 ? (
          <div className="text-center py-12">
            {mainTab === 'sent' ? (
              <>
                <Send className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {searchTerm ? 'No applications match your search' : 'No applications found'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm
                    ? 'Try adjusting your search terms'
                    : 'You haven\'t applied to any parcels or trips yet'
                  }
                </p>
                {searchTerm && (
                  <div className="mt-4">
                    <button
                      onClick={() => setSearchTerm('')}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Clear Search
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <Package className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {searchTerm ? 'No applications match your search' : 'No applications received'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm
                    ? 'Try adjusting your search terms'
                    : 'You haven\'t received any applications for your trips or parcels yet'
                  }
                </p>
                {searchTerm && (
                  <div className="mt-4">
                    <button
                      onClick={() => setSearchTerm('')}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Clear Search
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredApplications?.map((application) => (
              <ApplicationCard
                key={application.id}
                application={application}
                mainTab={mainTab}
                onCancel={handleCancelApplication}
                onAccept={handleAcceptApplication}
                onReject={handleRejectApplication}
                onDelete={handleDeleteApplication}
                isCancelling={cancelApplicationMutation.isLoading}
                isAccepting={acceptApplicationMutation.isLoading}
                isRejecting={rejectApplicationMutation.isLoading}
                isDeleting={deleteApplicationMutation.isLoading}
                navigate={navigate}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {(filteredApplications.length > 0 || true) && ( // Temporarily always show for testing
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm" style={{ color: colors.text.secondary }}>
              <span>Showing {startItem}-{endItem} of {filteredApplications.length} applications</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                className="px-2 py-1 rounded border text-sm"
                style={{
                  borderColor: colors.border,
                  backgroundColor: colors.neutral[50],
                  color: colors.text.primary
                }}
              >
                <option value={12}>12 per page</option>
                <option value={24}>24 per page</option>
                <option value={48}>48 per page</option>
              </select>
            </div>

            {/* Page navigation - only show if multiple pages */}
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    color: currentPage === 1 ? colors.text.tertiary : colors.text.secondary,
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== 1) {
                      e.currentTarget.style.backgroundColor = colors.neutral[100];
                      e.currentTarget.style.color = colors.primary[600];
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = currentPage === 1 ? colors.text.tertiary : colors.text.secondary;
                  }}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === pageNum
                            ? 'text-white'
                            : 'text-secondary hover:text-primary'
                        }`}
                        style={{
                          backgroundColor: currentPage === pageNum ? colors.primary[600] : 'transparent',
                          color: currentPage === pageNum ? 'white' : colors.text.secondary
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage !== pageNum) {
                            e.currentTarget.style.backgroundColor = colors.neutral[100];
                            e.currentTarget.style.color = colors.primary[600];
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = currentPage === pageNum ? colors.primary[600] : 'transparent';
                          e.currentTarget.style.color = currentPage === pageNum ? 'white' : colors.text.secondary;
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    color: currentPage === totalPages ? colors.text.tertiary : colors.text.secondary,
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== totalPages) {
                      e.currentTarget.style.backgroundColor = colors.neutral[100];
                      e.currentTarget.style.color = colors.primary[600];
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = currentPage === totalPages ? colors.text.tertiary : colors.text.secondary;
                  }}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <ConfirmationModal
          isOpen={showConfirmationModal}
          onClose={() => setShowConfirmationModal(false)}
          onConfirm={confirmationModalConfig.onConfirm}
          title={confirmationModalConfig.title}
          message={confirmationModalConfig.message}
          confirmText={confirmationModalConfig.confirmText}
          cancelText={confirmationModalConfig.cancelText}
          type={confirmationModalConfig.type}
          showInput={confirmationModalConfig.showInput}
          inputPlaceholder={confirmationModalConfig.inputPlaceholder}
          inputValue={confirmationModalConfig.inputValue}
          onInputChange={confirmationModalConfig.onInputChange}
          additionalInfo={confirmationModalConfig.additionalInfo}
          isLoading={cancelApplicationMutation.isLoading || acceptApplicationMutation.isLoading || rejectApplicationMutation.isLoading}
        />
      )}
    </>
  );
};

// Application Card Component
const ApplicationCard = ({ 
  application, 
  onCancel, 
  onAccept, 
  onReject, 
  onDelete,
  isCancelling, 
  isAccepting, 
  isRejecting,
  isDeleting,
  mainTab,
  navigate 
}) => {
  const colors = useThemeColors();

  const handleCardClick = () => {
    const path = application.target_type === 'parcel' 
      ? `/parcels/${application.target_id}` 
      : `/trips/${application.target_id}`;
    navigate(path);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return { bg: colors.warning[50], text: colors.warning[700], border: colors.warning[200] };
      case 'accepted':
        return { bg: colors.success[50], text: colors.success[700], border: colors.success[200] };
      case 'rejected':
        return { bg: colors.error[50], text: colors.error[700], border: colors.error[200] };
      case 'cancelled':
        return { bg: colors.neutral[50], text: colors.neutral[700], border: colors.neutral[200] };
      default:
        return { bg: colors.neutral[50], text: colors.neutral[700], border: colors.neutral[200] };
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'accepted':
        return <CheckCircle className="h-4 w-4" />;
      case 'rejected':
        return <AlertCircle className="h-4 w-4" />;
      case 'cancelled':
        return <X className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  // Check if target (parcel or trip) is expired
  const checkExpired = (target) => {
    if (!target) return false;
    if (target.status === 'expired' || target.status === 'cancelled') return true;
    if (target.departure_date && new Date(target.departure_date) <= new Date()) return true;
    if (target.expires_at && new Date(target.expires_at) <= new Date()) return true;
    return false;
  };
  
  const isTargetExpired = checkExpired(application.parcel) || checkExpired(application.trip);

  const canCancel = mainTab === 'sent' && application.status === 'pending' && !isTargetExpired;
  const canAccept = mainTab === 'received' && application.status === 'pending' && !isTargetExpired;
  const canReject = mainTab === 'received' && application.status === 'pending' && !isTargetExpired;

  const statusColors = getStatusColor(application.status);

  const daysSinceCreation = Math.ceil((new Date() - new Date(application.created_at)) / (1000 * 60 * 60 * 24));

  const formatValue = (value) => {
    if (!value) return 'N/A';
    const num = parseFloat(value);
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
      maximumFractionDigits: 0
    }).format(num);
  };

  return (
    <div
      className="group shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer border rounded-2xl"
      style={{
        backgroundColor: colors.card,
        borderColor: colors.border
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = colors.primary[300];
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = colors.border;
      }}
      onClick={handleCardClick}
    >
      {/* Content */}
      <div className="p-6">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center border"
                style={{
                  backgroundColor: colors.primary[50],
                  borderColor: colors.primary[200]
                }}
              >
                {mainTab === 'sent' ? (
                  <Send className="h-5 w-5" style={{ color: colors.primary[600] }} />
                ) : (
                  <Package className="h-5 w-5" style={{ color: colors.primary[600] }} />
                )}
              </div>
              <div>
                <h3 className="font-semibold capitalize" style={{ color: colors.text.primary }}>
                  {application?.target_type} Application
                </h3>
                <p className="text-xs" style={{ color: colors.text.tertiary }}>
                  ID: #{application.id}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border"
                style={{
                  backgroundColor: statusColors.bg,
                  color: statusColors.text,
                  borderColor: statusColors.border
                }}
              >
                {getStatusIcon(application.status)}
                <span className="ml-1 capitalize">{application.status}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Target Details */}
        <div className="mb-6">
          {application?.target_type === 'parcel' ? (
            <div className="flex items-center justify-center">
              <div className="flex-1">
                <div className="flex items-center" style={{ color: colors.text.secondary }}>
                  <MapPin className="h-4 w-4 mr-2" style={{ color: colors.success[500] }} />
                  <div>
                    <p className="text-xs uppercase tracking-wide" style={{ color: colors.text.tertiary }}>From</p>
                    <p className="font-semibold truncate" style={{ color: colors.text.primary }}>
                      {application?.parcel?.pickup_city}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex-1 text-center px-4">
                <div className="flex items-center justify-center" style={{ color: colors.text.tertiary }}>
                  <div style={{ width: '32px', height: '1px', backgroundColor: colors.border }}></div>
                  <ArrowRight className="h-4 w-4 mx-2" />
                  <div style={{ width: '32px', height: '1px', backgroundColor: colors.border }}></div>
                </div>
              </div>

              <div className="flex-1">
                <div className="flex items-center justify-end" style={{ color: colors.text.secondary }}>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wide" style={{ color: colors.text.tertiary }}>To</p>
                    <p className="font-semibold truncate" style={{ color: colors.text.primary }}>
                      {application?.parcel?.dropoff_city}
                    </p>
                  </div>
                  <MapPin className="h-4 w-4 ml-2" style={{ color: colors.error[500] }} />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <div className="flex-1">
                <div className="flex items-center" style={{ color: colors.text.secondary }}>
                  <MapPin className="h-4 w-4 mr-2" style={{ color: colors.success[500] }} />
                  <div>
                    <p className="text-xs uppercase tracking-wide" style={{ color: colors.text.tertiary }}>From</p>
                    <p className="font-semibold truncate" style={{ color: colors.text.primary }}>
                      {application.trip?.origin_city}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex-1 text-center px-4">
                <div className="flex items-center justify-center" style={{ color: colors.text.tertiary }}>
                  <div style={{ width: '32px', height: '1px', backgroundColor: colors.border }}></div>
                  <ArrowRight className="h-4 w-4 mx-2" />
                  <div style={{ width: '32px', height: '1px', backgroundColor: colors.border }}></div>
                </div>
              </div>

              <div className="flex-1">
                <div className="flex items-center justify-end" style={{ color: colors.text.secondary }}>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wide" style={{ color: colors.text.tertiary }}>To</p>
                    <p className="font-semibold truncate" style={{ color: colors.text.primary }}>
                      {application?.trip?.destination_city}
                    </p>
                  </div>
                  <MapPin className="h-4 w-4 ml-2" style={{ color: colors.error[500] }} />
                </div>
              </div>
            </div>
          )}
        </div>

        <hr className="my-4" style={{ borderColor: colors.border }} />

        {/* Applicant Details - Only for received applications */}
        {mainTab === 'received' && (
          <div className="mb-4">
            <div className="items-center justify-center">
              <div className="flex-1">
                <div className="flex items-center" style={{ color: colors.text.secondary }}>
                  <User className="h-4 w-4 mr-2" style={{ color: colors.primary[500] }} />
                  <div>
                    <p className="text-xs uppercase tracking-wide" style={{ color: colors.text.tertiary }}>Applicant</p>
                    <p className="font-semibold truncate" style={{ color: colors.text.primary }}>
                      {application?.applicant_name}
                    </p>
                  </div>
                </div>
              </div>
{/* 
              <div className="flex-1 text-center px-4">
                <div className="flex items-center justify-center" style={{ color: colors.text.tertiary }}>
                  <div style={{ width: '32px', height: '1px', backgroundColor: colors.border }}></div>
                  <ArrowRight className="h-4 w-4 mx-2" />
                  <div style={{ width: '32px', height: '1px', backgroundColor: colors.border }}></div>
                </div>
              </div> */}

              <div className="flex-1">
                <div className="flex items-center justify-end" style={{ color: colors.text.secondary }}>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wide" style={{ color: colors.text.tertiary }}>Contact</p>
                    <p className="font-semibold truncate" style={{ color: colors.text.primary }}>
                      {application.applicant_email}
                    </p>
                  </div>
                  <Mail className="h-4 w-4 ml-2" style={{ color: colors.primary[500] }} />
                </div>
              </div>
            </div>
          </div>
        )}

        <hr className="my-4" style={{ borderColor: colors.border }} />

        {/* Key metrics */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="text-center p-2 rounded-xl" style={{ backgroundColor: colors.neutral[50] }}>
            <Timer className="h-4 w-4 mx-auto mb-1" style={{ color: colors.warning[500] }} />
            <p className="text-xs" style={{ color: colors.text.tertiary }}>Applied</p>
            <p className="font-bold text-sm" style={{ color: colors.text.primary }}>
              {daysSinceCreation}d ago
            </p>
          </div>

          {application.declared_amount ? (
            <div className="text-center p-2 rounded-xl" style={{ backgroundColor: colors.neutral[50] }}>
              <DollarSign className="h-4 w-4 mx-auto mb-1" style={{ color: colors.success[500] }} />
              <p className="text-xs" style={{ color: colors.text.tertiary }}>Declared</p>
              <p className="font-bold text-sm" style={{ color: colors.text.primary }}>
                {formatValue(application?.declared_amount)}
              </p>
            </div>
          ) : (
            <div className="text-center p-2 rounded-xl" style={{ backgroundColor: colors.neutral[50] }}>
              <User className="h-4 w-4 mx-auto mb-1" style={{ color: colors.primary[500] }} />
              <p className="text-xs" style={{ color: colors.text.tertiary }}>Applicant</p>
              <p className="font-bold text-sm truncate" style={{ color: colors.text.primary }}>
                {mainTab === 'received' ? application.applicant_name : 'You'}
              </p>
            </div>
          )}
        </div>

        {/* Additional financial details */}
        {(application.purchase_amount || application.delivery_amount) && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            {application.purchase_amount && (
              <div className="text-center p-2 rounded-xl" style={{ backgroundColor: colors.neutral[50] }}>
                <Package className="h-4 w-4 mx-auto mb-1" style={{ color: colors.primary[500] }} />
                <p className="text-xs" style={{ color: colors.text.tertiary }}>Purchase</p>
                <p className="font-bold text-sm" style={{ color: colors.text.primary }}>
                  {formatValue(application?.purchase_amount)}
                </p>
              </div>
            )}

            {application.delivery_amount && (
              <div className="text-center p-2 rounded-xl" style={{ backgroundColor: colors.neutral[50] }}>
                <Truck className="h-4 w-4 mx-auto mb-1" style={{ color: colors.warning[500] }} />
                <p className="text-xs" style={{ color: colors.text.tertiary }}>Delivery</p>
                <p className="font-bold text-sm" style={{ color: colors.text.primary }}>
                  {formatValue(application?.delivery_amount)}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 mt-4">
          {/* Cancel/Accept/Reject buttons for specific cases */}
          {(canCancel || canAccept || canReject) && (
            <div className="flex gap-2 flex-1">
              {canCancel && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel(application.id);
                  }}
                  disabled={isCancelling}
                  className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50"
                  style={{
                    borderColor: colors.error[500],
                    color: colors.error[600],
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.stopPropagation();
                    e.target.style.backgroundColor = colors.error[500];
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.stopPropagation();
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = colors.error[600];
                  }}
                >
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </button>
              )}

              {canAccept && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAccept(application.id);
                  }}
                  disabled={isAccepting}
                  className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50"
                  style={{
                    borderColor: colors.success[500],
                    color: colors.success[600],
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.stopPropagation();
                    e.target.style.backgroundColor = colors.success[500];
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.stopPropagation();
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = colors.success[600];
                  }}
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Accept
                </button>
              )}

              {canReject && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onReject(application.id);
                  }}
                  disabled={isRejecting}
                  className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50"
                  style={{
                    borderColor: colors.error[500],
                    color: colors.error[600],
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.stopPropagation();
                    e.target.style.backgroundColor = colors.error[500];
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.stopPropagation();
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = colors.error[600];
                  }}
                >
                  <X className="h-4 w-4 mr-1" />
                  Reject
                </button>
              )}
            </div>
          )}

          {/* Delete button for sent applications when target is expired */}
          {mainTab === 'sent' && isTargetExpired && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(application.id);
              }}
              disabled={isDeleting}
              className="w-full flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50"
              style={{
                borderColor: colors.neutral[400],
                color: colors.neutral[600],
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.stopPropagation();
                e.target.style.backgroundColor = colors.neutral[400];
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.stopPropagation();
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = colors.neutral[600];
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Application
            </button>
          )}
        </div>

        {/* Status-specific information */}
        {mainTab === 'sent' && application.status === 'accepted' && (
          <div className="mt-4 p-3 rounded-lg" style={{
            backgroundColor: colors.success[50],
            border: `1px solid ${colors.success[200]}`
          }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center text-sm" style={{ color: colors.success[700] }}>
                <CheckCircle className="h-4 w-4 mr-2" />
                <span>Congratulations! Your application has been accepted.</span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Navigate to agreements page or show agreement details
                  window.location.href = '/agreements';
                }}
                className="ml-3 px-3 py-1 text-xs font-medium rounded-md transition-colors"
                style={{
                  backgroundColor: colors.primary[500],
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = colors.primary[600];
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = colors.primary[500];
                }}
              >
                View Agreement
              </button>
            </div>
          </div>
        )}

        {mainTab === 'received' && application.status === 'accepted' && (
          <div className="mt-4 p-3 rounded-lg" style={{
            backgroundColor: colors.success[50],
            border: `1px solid ${colors.success[200]}`
          }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center text-sm" style={{ color: colors.success[700] }}>
                <CheckCircle className="h-4 w-4 mr-2" />
                <span>You have accepted this application.</span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Navigate to agreements page or show agreement details
                  window.location.href = '/agreements';
                }}
                className="ml-3 px-3 py-1 text-xs font-medium rounded-md transition-colors"
                style={{
                  backgroundColor: colors.primary[500],
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = colors.primary[600];
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = colors.primary[500];
                }}
              >
                View Agreement
              </button>
            </div>
          </div>
        )}

        {application.status === 'rejected' && (
          <div className="mt-4 p-3 rounded-lg" style={{
            backgroundColor: colors.error[50],
            border: `1px solid ${colors.error[200]}`
          }}>
            <div className="flex items-center text-sm" style={{ color: colors.error[700] }}>
              <AlertCircle className="h-4 w-4 mr-2" />
              <span>This application has been rejected.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Applications;
